import { useState, type ReactNode } from 'react';
import { Drawer } from '../../design-system/Overlay';
import { Avatar, Pill } from '../../design-system/primitives';
import { formatRoute } from '../../app/router';
import { useAppServices } from '../../views/ViewContext';
import { PanelHistory } from './PanelHistory';
import { CommandError } from './TenantPage';
import { errorMessage, formatAddress, formatDate, formatIdentifier, text, type Address, type EntityRow, type EstablishmentRow, type SiteRow, type TenantProfile, type VocabularyEntry, type Warning } from './tenant-data';

/**
 * Sociétés, sites et établissements (ADR-128 §2). Une société est l'entité juridique (SIREN, TVA) ; un
 * site est un lieu physique, que plusieurs sociétés peuvent partager ; un établissement est la présence
 * d'une société sur un site (SIRET, rôles, agréments facultatifs). Deux onglets : les sociétés, et les sites avec
 * les sociétés qui y sont présentes (maquette validée le 2026-10-15 : la présence se gère depuis le site). Création et modification s'ouvrent dans un panneau à droite (demande de
 * l'utilisateur du 2026-10-14). Un identifiant invalide est enregistré et signalé en rouge, jamais refusé.
 */
type Editing = { kind: 'entity'; id?: string } | { kind: 'site'; id?: string } | { kind: 'establishment'; site_id: string; entity_id?: string; id?: string } | { kind: 'close'; id: string } | null;

const compact = (record: Record<string, string>): Record<string, string> => Object.fromEntries(Object.entries(record).filter(([, value]) => value.trim() !== '').map(([key, value]) => [key, value.trim()]));
/** Libellé d'une valeur de vocabulaire déclarée (rôle, agrément, nature de site), sinon la valeur. */
const roleLabel = (vocabulary: readonly VocabularyEntry[], value: string): string => text(vocabulary.find((entry) => entry.value === value)?.label, value);

function useCommand(onDone: () => void) {
  const services = useAppServices();
  const [error, setError] = useState<{ message: string; elevation: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (name: string, input: Parameters<typeof services.client.command>[1]): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await services.client.command(name, input);
      onDone();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };
  return { error, busy, run };
}

/** Valeur lue avec son avertissement éventuel : en rouge, le message à côté. */
function Checked({ value, warning, mono = true }: { value: string; warning: Warning | undefined; mono?: boolean }) {
  return (
    <span className={`${mono ? 'ay-tenant__mono' : ''}${warning === undefined ? '' : ' ay-tenant__invalid'}`}>
      {value}
      {warning !== undefined && <span className="ay-tenant__invalid-note"> — {warning.message}</span>}
    </span>
  );
}

/** Panneau à droite des créations et modifications : un seul ouvert à la fois. */
function EditingDrawer({ profile, editing, onClose, onDone, onEdit }: { profile: TenantProfile; editing: Editing; onClose: () => void; onDone: () => void; onEdit: (value: Editing) => void }) {
  const entity = editing?.kind === 'entity' && editing.id !== undefined ? profile.entities.find((entry) => entry.id === editing.id) : undefined;
  const site = editing?.kind === 'site' && editing.id !== undefined ? profile.sites.find((entry) => entry.id === editing.id) : undefined;
  const owner = editing?.kind === 'establishment' && editing.entity_id !== undefined ? profile.entities.find((entry) => entry.id === editing.entity_id) : undefined;
  const place = editing?.kind === 'establishment' ? profile.sites.find((entry) => entry.id === editing.site_id) : undefined;
  const establishment = (editing?.kind === 'establishment' || editing?.kind === 'close') && editing.id !== undefined ? profile.establishments.find((entry) => entry.id === editing.id) : undefined;
  const siteName = (id: string | undefined): string => profile.sites.find((entry) => entry.id === id)?.display_name ?? '';
  let title = '';
  let kicker = '';
  let history: ReactNode = null;
  let content: ReactNode = null;
  if (editing?.kind === 'entity') {
    title = entity === undefined ? 'Nouvelle société' : `Modifier ${entity.display_name}`;
    kicker = entity === undefined ? 'Création' : 'Société';
    if (entity !== undefined) history = <PanelHistory type="Entity" id={entity.id} version={entity.version} />;
    content = <EntityForm entity={entity} onDone={onDone} onCancel={onClose} />;
  } else if (editing?.kind === 'site') {
    title = site === undefined ? 'Nouveau site' : `Modifier le site ${site.display_name}`;
    kicker = site === undefined ? 'Création' : 'Site';
    if (site !== undefined) history = <PanelHistory type="Site" id={site.id} version={site.version} />;
    content = <SiteForm profile={profile} site={site} onDone={onDone} onCancel={onClose} />;
  } else if (editing?.kind === 'establishment' && place !== undefined) {
    title = owner === undefined ? `Ajouter une société sur ${place.display_name}` : `${owner.display_name} sur ${place.display_name}`;
    kicker = establishment === undefined ? 'Présence sur le site' : 'Présence';
    if (establishment !== undefined) history = <PanelHistory type="Establishment" id={establishment.id} version={establishment.version} />;
    content = <EstablishmentForm profile={profile} site={place} entity={owner} establishment={establishment} onDone={onDone} onCancel={onClose} onCloseEstablishment={establishment === undefined ? undefined : () => onEdit({ kind: 'close', id: establishment.id })} />;
  } else if (editing?.kind === 'close' && establishment !== undefined) {
    kicker = 'Présence';
    title = `Fermer la présence de ${profile.entities.find((entry) => entry.id === establishment.entity_id)?.display_name ?? '—'} sur ${siteName(establishment.site_id)}`;
    content = <CloseForm establishment={establishment} onDone={onDone} onCancel={onClose} />;
  }
  return (
    <Drawer title={title} kicker={kicker} open={content !== null} onClose={onClose} modal>
      <div className="ay-tenant__panel">
        {content}
        {history}
      </div>
    </Drawer>
  );
}

export function OrganizationTab({ profile, onSaved, view }: { profile: TenantProfile; onSaved: () => void; view: 'companies' | 'sites' }) {
  const [editing, setEditing] = useState<Editing>(null);
  const done = (): void => {
    setEditing(null);
    onSaved();
  };
  const siteOf = (id: string): SiteRow | undefined => profile.sites.find((site) => site.id === id);
  const entityOf = (id: string): EntityRow | undefined => profile.entities.find((entity) => entity.id === id);
  const drawer = <EditingDrawer profile={profile} editing={editing} onClose={() => setEditing(null)} onDone={done} onEdit={setEditing} />;
  if (view === 'sites') {
    return (
      <div className="ay-tenant__stack">
        <div className="ay-tenant__intro">
          <p>
            Un <strong>site</strong> est un lieu physique, que plusieurs sociétés peuvent partager. Chaque société présente y a son rôle, son SIRET et, s’il y a lieu, son agrément.
          </p>
          <button type="button" className="ay-button ay-button--dark" aria-haspopup="dialog" onClick={() => setEditing({ kind: 'site' })}>
            Ajouter un site
          </button>
        </div>
        {profile.sites.length === 0 && <p className="ay-text ay-text--muted">Aucun site déclaré.</p>}
        {profile.sites.map((site) => (
          <SiteCard key={site.id} profile={profile} site={site} entityOf={entityOf} setEditing={setEditing} />
        ))}
        {/* Les zones sont des lieux d'un site : elles ne se listent pas ici (remarque de l'utilisateur du 2026-10-14). */}
        {profile.unattached_site_locations.length > 0 && (
          <p className="ay-text ay-text--muted">Emplacements de nature site à rattacher à un site : {profile.unattached_site_locations.map((entry) => entry.display_name).join(', ')}.</p>
        )}
        {drawer}
      </div>
    );
  }
  return (
    <div className="ay-tenant__stack">
      <div className="ay-tenant__intro">
        <p>
          Une <strong>société</strong> est une entité juridique (SIREN, TVA). Sa présence sur chaque site, avec son rôle et son agrément, se gère dans l’onglet <a href={formatRoute({ kind: 'tenant', tab: 'sites' })}>Sites</a>.
        </p>
        <button type="button" className="ay-button ay-button--dark" aria-haspopup="dialog" onClick={() => setEditing({ kind: 'entity' })}>
          Ajouter une société
        </button>
      </div>

      {profile.entities.map((entity) => {
        const presences = profile.establishments.filter((entry) => entry.entity_id === entity.id && entry.status === 'active');
        const warning = (key: string): Warning | undefined => entity.warnings.find((entry) => entry.field === `legal_identifiers.${key}`);
        const ids = entity.legal_identifiers ?? {};
        return (
          <section key={entity.id} className="ay-card ay-tenant__company" aria-labelledby={`ay-company-${entity.code}`}>
            <div className="ay-tenant__cardhead">
              <Avatar label={entity.display_name} size="sm" />
              <h2 id={`ay-company-${entity.code}`}>{entity.display_name}</h2>
              {entity.is_default && <Pill>par défaut</Pill>}
              {entity.status === 'inactive' && <Pill tone="warn">inactive</Pill>}
              {entity.status === 'ceased' && (
                <Pill tone="warn">
                  cessée le {civilDateLabel(entity.ceased_on)}
                  {entity.successor_entity_id === undefined ? '' : ` · reprise par ${profile.entities.find((candidate) => candidate.id === entity.successor_entity_id)?.display_name ?? '—'}`}
                </Pill>
              )}
              {entity.warnings.length > 0 && <Pill tone="danger">à vérifier</Pill>}
              <span className="ay-tenant__grow" />
              <button type="button" className="ay-button ay-button--ghost" aria-haspopup="dialog" onClick={() => setEditing({ kind: 'entity', id: entity.id })}>
                Modifier la société
              </button>
            </div>
            <dl className="ay-tenant__grid">
              <Fact label="Raison sociale">{entity.legal_name ?? entity.display_name}</Fact>
              <Fact label="Forme juridique">{entity.legal_form ?? '—'}</Fact>
              <Fact label="SIREN">
                <Checked value={formatIdentifier('siren', ids['siren'])} warning={warning('siren')} />
              </Fact>
              <Fact label="N° TVA">
                <Checked value={formatIdentifier('vat', ids['vat'])} warning={warning('vat')} />
              </Fact>
              <Fact label="Code d’activité">{entity.activity_code === undefined ? '—' : `${entity.activity_code}${entity.activity_label === undefined ? '' : ` — ${entity.activity_label}`}`}</Fact>
              <Fact label="Siège">{formatAddress(entity.head_office_address) || '—'}</Fact>
              <Fact label="Présente sur">
                {presences.length === 0
                  ? '—'
                  : presences
                      .map((entry) => {
                        const roles = (entry.roles ?? []).map((role) => roleLabel(profile.vocabulary.roles, role).toLowerCase()).join(', ');
                        return `${siteOf(entry.site_id)?.display_name ?? '—'}${roles === '' ? '' : ` (${roles})`}`;
                      })
                      .join(' · ')}
              </Fact>
            </dl>
          </section>
        );
      })}
      {drawer}
    </div>
  );
}

/** Un site et les sociétés qui y sont présentes (maquette validée le 2026-10-15) : une présence déclarée est un établissement. */
function SiteCard({ profile, site, entityOf, setEditing }: { profile: TenantProfile; site: SiteRow; entityOf: (id: string) => EntityRow | undefined; setEditing: (value: Editing) => void }) {
  const establishments = profile.establishments.filter((entry) => entry.site_id === site.id);
  // Une société présente sur le site sans établissement ouvert : sa présence reste à déclarer.
  const undeclared = site.entity_ids.filter((id) => !establishments.some((entry) => entry.entity_id === id && entry.status === 'active'));
  const facts = [site.site_kind === undefined ? undefined : roleLabel(profile.vocabulary.site_kinds, site.site_kind), formatAddress(site.address) || undefined, `${site.location_count} lieu${site.location_count > 1 ? 'x' : ''}`, site.gln === undefined ? undefined : `GLN ${site.gln}`].filter((fact): fact is string => fact !== undefined);
  return (
    <section className="ay-card ay-tenant__company" aria-labelledby={`ay-site-${site.code}`}>
      <div className="ay-tenant__cardhead">
        <h2 id={`ay-site-${site.code}`}>{site.display_name}</h2>
        {site.status === 'inactive' && <Pill tone="warn">inactif</Pill>}
        {site.status === 'closed' && <Pill tone="warn">fermé le {civilDateLabel(site.closed_on)}</Pill>}
        <span className="ay-tenant__grow" />
        <button type="button" className="ay-button ay-button--ghost" aria-haspopup="dialog" onClick={() => setEditing({ kind: 'site', id: site.id })}>
          Modifier le site
        </button>
      </div>
      <p className="ay-text ay-text--muted">{facts.join(' · ')}</p>
      <div className="ay-tenant__subhead">
        <strong>Sociétés présentes</strong>
        <span className="ay-tenant__count">{establishments.filter((entry) => entry.status === 'active').length + undeclared.length}</span>
        <span className="ay-tenant__grow" />
        {site.status === 'active' && (
          <button type="button" className="ay-button ay-button--soft" aria-haspopup="dialog" onClick={() => setEditing({ kind: 'establishment', site_id: site.id })}>
            Ajouter une société sur ce site
          </button>
        )}
      </div>
      {establishments.length === 0 && undeclared.length === 0 ? (
        <p className="ay-text ay-text--muted">Aucune société présente.</p>
      ) : (
        <div className="ay-table-wrap">
          <table className="ay-table">
            <thead>
              <tr>
                <th scope="col">Société</th>
                <th scope="col">Rôles sur le site</th>
                <th scope="col">SIRET</th>
                <th scope="col">Agréments</th>
                <th scope="col">
                  <span className="ay-visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {establishments.map((establishment) => (
                <EstablishmentLine key={establishment.id} profile={profile} establishment={establishment} entity={entityOf(establishment.entity_id)} setEditing={setEditing} />
              ))}
              {undeclared.map((id) => {
                const entity = entityOf(id);
                return entity === undefined ? null : (
                  <tr key={id}>
                    <td>
                      <EntityName entity={entity} />
                    </td>
                    <td colSpan={3} className="ay-text--muted">
                      Présente, rôle à déclarer
                    </td>
                    <td className="ay-tenant__right">
                      <button type="button" className="ay-button ay-button--ghost" aria-haspopup="dialog" onClick={() => setEditing({ kind: 'establishment', site_id: site.id, entity_id: id })}>
                        Déclarer
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function EntityName({ entity }: { entity: EntityRow | undefined }) {
  return (
    <span className="ay-tenant__chips">
      {entity !== undefined && <Avatar label={entity.display_name} size="xs" />}
      <strong>{entity?.display_name ?? '—'}</strong>
    </span>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function EstablishmentLine({ profile, entity, establishment, setEditing }: { profile: TenantProfile; entity: EntityRow | undefined; establishment: EstablishmentRow; setEditing: (value: Editing) => void }) {
  const kinds = profile.vocabulary.approval_kinds;
  const siretWarning = establishment.warnings.find((entry) => entry.field === 'legal_identifiers.siret');
  const closed = establishment.status !== 'active';
  const approvals = Object.entries(establishment.approval_numbers ?? {});
  return (
    <tr className={closed ? 'ay-tenant__closed' : undefined}>
      <td>
        <EntityName entity={entity} />
        {closed && <div className="ay-text ay-text--muted">Fermé le {civilDateLabel(establishment.closed_on)}</div>}
      </td>
      <td>{(establishment.roles ?? []).map((role) => roleLabel(profile.vocabulary.roles, role)).join(', ') || '—'}</td>
      <td>
        <Checked value={formatIdentifier('siret', establishment.legal_identifiers?.['siret'])} warning={siretWarning} />
      </td>
      <td>
        {approvals.length === 0
          ? '—'
          : approvals.map(([kind, number]) => (
              <div key={kind}>
                <span className="ay-tenant__mono">{number}</span> <span className="ay-text--muted">{roleLabel(kinds, kind)}</span>
              </div>
            ))}
      </td>
      <td className="ay-tenant__right">
        {!closed && (
          <span className="ay-tenant__rowactions">
            <button type="button" className="ay-button ay-button--ghost" aria-haspopup="dialog" onClick={() => setEditing({ kind: 'establishment', site_id: establishment.site_id, entity_id: establishment.entity_id, id: establishment.id })}>
              Modifier
            </button>
            <button type="button" className="ay-button ay-button--ghost" aria-haspopup="dialog" onClick={() => setEditing({ kind: 'close', id: establishment.id })}>
              Fermer
            </button>
          </span>
        )}
      </td>
    </tr>
  );
}

function AddressFields({ value, onChange, legend }: { value: Address; onChange: (address: Address) => void; legend: string }) {
  return (
    <fieldset className="ay-tenant__fieldset ay-tenant__address ay-tenant__wide">
      <legend>{legend}</legend>
      <label className="ay-field ay-tenant__wide">
        <span className="ay-field__label">Rue</span>
        <input value={value.lines?.[0] ?? ''} onChange={(event) => onChange({ ...value, lines: event.target.value === '' ? [] : [event.target.value] })} />
      </label>
      <label className="ay-field">
        <span className="ay-field__label">Code postal</span>
        <input value={value.postal_code ?? ''} onChange={(event) => onChange({ ...value, postal_code: event.target.value })} />
      </label>
      <label className="ay-field">
        <span className="ay-field__label">Ville</span>
        <input value={value.city ?? ''} onChange={(event) => onChange({ ...value, city: event.target.value })} />
      </label>
      <label className="ay-field">
        <span className="ay-field__label">Pays</span>
        <input value={value.country} maxLength={2} pattern="[A-Z]{2}" onChange={(event) => onChange({ ...value, country: event.target.value.toUpperCase() })} />
      </label>
    </fieldset>
  );
}

const cleanAddress = (address: Address): Address | undefined => {
  const lines = (address.lines ?? []).map((line) => line.trim()).filter((line) => line !== '');
  const out: Address = { country: address.country.trim() || 'FR', ...(lines.length === 0 ? {} : { lines }) };
  if (address.postal_code?.trim()) out.postal_code = address.postal_code.trim();
  if (address.city?.trim()) out.city = address.city.trim();
  return lines.length === 0 && out.city === undefined && out.postal_code === undefined ? undefined : out;
};

function FormActions({ busy, onCancel, disabled = false, destructive }: { busy: boolean; onCancel: () => void; disabled?: boolean; destructive?: ReactNode }) {
  return (
    <div className="ay-tenant__actions ay-tenant__wide">
      {destructive}
      <button type="button" className="ay-button ay-button--ghost" onClick={onCancel}>
        Annuler
      </button>
      <button type="submit" className="ay-button ay-button--primary" disabled={busy || disabled}>
        Enregistrer
      </button>
    </div>
  );
}

function EntityForm({ entity, onDone, onCancel }: { entity?: EntityRow | undefined; onDone: () => void; onCancel: () => void }) {
  const { error, busy, run } = useCommand(onDone);
  const ids = entity?.legal_identifiers ?? {};
  const [fields, setFields] = useState({ code: entity?.code ?? '', display_name: entity?.display_name ?? '', legal_name: entity?.legal_name ?? '', legal_form: entity?.legal_form ?? '', siren: ids['siren'] ?? '', vat: ids['vat'] ?? '', activity_code: entity?.activity_code ?? '', activity_label: entity?.activity_label ?? '' });
  const [address, setAddress] = useState<Address>(entity?.head_office_address ?? { country: 'FR' });
  const set = (key: keyof typeof fields) => (event: { target: { value: string } }) => setFields({ ...fields, [key]: event.target.value });
  const submit = (): Promise<void> => {
    const others = Object.fromEntries(Object.entries(ids).filter(([key]) => key !== 'siren' && key !== 'vat'));
    const identifiers = compact({ ...others, siren: fields.siren, vat: fields.vat });
    const head = cleanAddress(address);
    const payload: Record<string, unknown> = {
      display_name: fields.display_name.trim(),
      ...compact({ legal_name: fields.legal_name, legal_form: fields.legal_form, activity_code: fields.activity_code, activity_label: fields.activity_label }),
      ...(Object.keys(identifiers).length === 0 ? {} : { legal_identifiers: identifiers }),
      ...(head === undefined ? {} : { head_office_address: head }),
    };
    return entity === undefined ? run('RegisterEntity', { payload: { code: fields.code.trim(), ...payload } }) : run('UpdateEntity', { payload, target: { type: 'Entity', id: entity.id }, expected_version: entity.version });
  };
  return (
    <form
      className="ay-tenant__form"
      aria-label={entity === undefined ? 'Nouvelle société' : `Modifier ${entity.display_name}`}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
<label className="ay-field ay-tenant__wide">
        <span className="ay-field__label">Nom affiché</span>
        <input required value={fields.display_name} onChange={set('display_name')} />
      </label>
      {entity === undefined && (
        <label className="ay-field">
          <span className="ay-field__label">Code</span>
          <input required pattern="[a-z0-9][a-z0-9_\-]{0,39}" title="minuscules, chiffres, tiret" value={fields.code} onChange={set('code')} />
        </label>
      )}
<label className="ay-field ay-tenant__wide">
        <span className="ay-field__label">Raison sociale</span>
        <input value={fields.legal_name} onChange={set('legal_name')} />
      </label>
      <label className="ay-field">
        <span className="ay-field__label">Forme juridique</span>
        <input value={fields.legal_form} maxLength={40} onChange={set('legal_form')} />
      </label>
      <label className="ay-field">
        <span className="ay-field__label">SIREN</span>
        <input className="ay-tenant__mono" inputMode="numeric" value={fields.siren} onChange={set('siren')} />
      </label>
      <label className="ay-field">
        <span className="ay-field__label">N° TVA</span>
        <input className="ay-tenant__mono" value={fields.vat} onChange={set('vat')} />
      </label>
      <label className="ay-field">
        <span className="ay-field__label">Code d’activité (NAF)</span>
        <input value={fields.activity_code} maxLength={20} onChange={set('activity_code')} />
      </label>
<label className="ay-field ay-tenant__wide">
        <span className="ay-field__label">Libellé de l’activité</span>
        <input value={fields.activity_label} onChange={set('activity_label')} />
      </label>
      <AddressFields legend="Siège" value={address} onChange={setAddress} />
      <p className="ay-text ay-text--muted ay-tenant__wide">Un identifiant dont la clé est fausse est enregistré et signalé en rouge.</p>
      <CommandError error={error} />
      <FormActions busy={busy} onCancel={onCancel} disabled={fields.display_name.trim() === '' || (entity === undefined && fields.code.trim() === '')} />
    </form>
  );
}

function SiteForm({ profile, site, onDone, onCancel }: { profile: TenantProfile; site?: SiteRow | undefined; onDone: () => void; onCancel: () => void }) {
  const { error, busy, run } = useCommand(onDone);
  const [fields, setFields] = useState({ code: site?.code ?? '', display_name: site?.display_name ?? '', site_kind: site?.site_kind ?? '', timezone: site?.timezone ?? '', gln: site?.gln ?? '', root: '' });
  const [address, setAddress] = useState<Address>(site?.address ?? { country: 'FR' });
  const [members, setMembers] = useState<string[]>(site?.entity_ids ?? []);
  const set = (key: keyof typeof fields) => (event: { target: { value: string } }) => setFields({ ...fields, [key]: event.target.value });
  // La première société cochée est la propriétaire ; sur un site existant, elle ne change pas.
  const owner = site?.entity_ids[0];
  const toggle = (id: string): void => {
    if (id === owner) return;
    setMembers(members.includes(id) ? members.filter((entry) => entry !== id) : [...members, id]);
  };
  const submit = (): Promise<void> => {
    const place = cleanAddress(address);
    const payload: Record<string, unknown> = {
      display_name: fields.display_name.trim(),
      entity_ids: members,
      ...compact({ site_kind: fields.site_kind, timezone: fields.timezone, gln: fields.gln }),
      ...(place === undefined ? {} : { address: place }),
      ...(fields.root === '' ? {} : { root_location_id: fields.root }),
    };
    return site === undefined ? run('RegisterSite', { payload: { code: fields.code.trim(), ...payload } }) : run('UpdateSite', { payload, target: { type: 'Site', id: site.id }, expected_version: site.version });
  };
  return (
    <form
      className="ay-tenant__form"
      aria-label={site === undefined ? 'Nouveau site' : `Modifier le site ${site.display_name}`}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
<label className="ay-field ay-tenant__wide">
        <span className="ay-field__label">Nom</span>
        <input required value={fields.display_name} onChange={set('display_name')} />
      </label>
      {site === undefined && (
        <label className="ay-field">
          <span className="ay-field__label">Code</span>
          <input required pattern="[A-Za-z0-9][A-Za-z0-9_\-]{0,39}" value={fields.code} onChange={set('code')} />
        </label>
      )}
      <label className="ay-field">
        <span className="ay-field__label">Nature</span>
        <select value={fields.site_kind} onChange={set('site_kind')}>
          <option value="">—</option>
          {profile.vocabulary.site_kinds.map((kind) => (
            <option key={kind.value} value={kind.value}>
              {text(kind.label, kind.value)}
            </option>
          ))}
        </select>
      </label>
      <label className="ay-field">
        <span className="ay-field__label">GLN (facultatif)</span>
        <input className="ay-tenant__mono" inputMode="numeric" pattern="[0-9]{13}" value={fields.gln} onChange={set('gln')} />
      </label>
      <label className="ay-field">
        <span className="ay-field__label">Fuseau (facultatif)</span>
        <input placeholder="celui du tenant" value={fields.timezone} onChange={set('timezone')} />
      </label>
      {(site === undefined || site.root_location === undefined) && profile.unattached_site_locations.length > 0 && (
        <label className="ay-field ay-tenant__wide">
          <span className="ay-field__label">Emplacement rattaché</span>
          <select value={fields.root} onChange={set('root')}>
            <option value="">Aucun</option>
            {profile.unattached_site_locations.map((location) => (
              <option key={location.id} value={location.id}>
                {location.display_name}
              </option>
            ))}
          </select>
        </label>
      )}
      <AddressFields legend="Adresse" value={address} onChange={setAddress} />
      <fieldset className="ay-tenant__fieldset ay-tenant__wide">
        <legend>Sociétés présentes</legend>
        {profile.entities.map((entity) => (
          <label key={entity.id} className="ay-tenant__check">
            <input type="checkbox" checked={members.includes(entity.id)} disabled={entity.id === owner} onChange={() => toggle(entity.id)} />
            {entity.display_name}
            {(entity.id === owner || (owner === undefined && members[0] === entity.id)) && <span className="ay-text--muted"> (propriétaire)</span>}
          </label>
        ))}
      </fieldset>
      <CommandError error={error} />
      <FormActions busy={busy} onCancel={onCancel} disabled={members.length === 0 || fields.display_name.trim() === '' || (site === undefined && fields.code.trim() === '')} />
    </form>
  );
}

/**
 * Présence d'une société sur un site (un établissement). Depuis un site : la société se choisit ; si elle n'est pas
 * encore parmi les sociétés du site, elle y est d'abord ajoutée (UpdateSite), puis l'établissement est enregistré.
 */
function EstablishmentForm({ profile, site, entity, establishment, onDone, onCancel, onCloseEstablishment }: { profile: TenantProfile; site: SiteRow; entity?: EntityRow | undefined; establishment?: EstablishmentRow | undefined; onDone: () => void; onCancel: () => void; onCloseEstablishment?: (() => void) | undefined }) {
  const services = useAppServices();
  const { error, busy, run } = useCommand(onDone);
  const declared = new Set(profile.establishments.filter((entry) => entry.site_id === site.id && entry.status === 'active').map((entry) => entry.entity_id));
  const candidates = profile.entities.filter((entry) => entry.status === 'active' && !declared.has(entry.id));
  const [entityId, setEntityId] = useState(entity?.id ?? candidates[0]?.id ?? '');
  const [roles, setRoles] = useState<string[]>(establishment?.roles ?? []);
  const [siret, setSiret] = useState(establishment?.legal_identifiers?.['siret'] ?? '');
  const [approvals, setApprovals] = useState<Record<string, string>>(establishment?.approval_numbers ?? {});
  const [openedOn, setOpenedOn] = useState(establishment?.opened_on ?? '');
  const [joinError, setJoinError] = useState<{ message: string; elevation: boolean } | null>(null);
  const submit = async (): Promise<void> => {
    const numbers = compact(approvals);
    const others = Object.fromEntries(Object.entries(establishment?.legal_identifiers ?? {}).filter(([key]) => key !== 'siret'));
    const identifiers = compact({ ...others, siret });
    const payload: Record<string, unknown> = {
      roles,
      ...(Object.keys(identifiers).length === 0 ? {} : { legal_identifiers: identifiers }),
      ...(Object.keys(numbers).length === 0 ? {} : { approval_numbers: numbers }),
      ...(openedOn === '' ? {} : { opened_on: openedOn }),
    };
    if (establishment !== undefined) return run('UpdateEstablishment', { payload, target: { type: 'Establishment', id: establishment.id }, expected_version: establishment.version });
    if (!site.entity_ids.includes(entityId)) {
      // La société rejoint d'abord le site ; si l'établissement échoue ensuite, elle reste présente, « rôle à déclarer ».
      setJoinError(null);
      try {
        await services.client.command('UpdateSite', { payload: { entity_ids: [...site.entity_ids, entityId] }, target: { type: 'Site', id: site.id }, expected_version: site.version });
      } catch (caught) {
        setJoinError(errorMessage(caught));
        return;
      }
    }
    return run('RegisterEstablishment', { payload: { entity_id: entityId, site_id: site.id, ...payload } });
  };
  if (establishment === undefined && entity === undefined && candidates.length === 0) {
    return (
      <p className="ay-text ay-text--muted">
        Toutes les sociétés sont déjà présentes sur {site.display_name}.{' '}
        <button type="button" className="ay-button ay-button--ghost" onClick={onCancel}>
          Fermer
        </button>
      </p>
    );
  }
  return (
    <form
      className="ay-tenant__form"
      aria-label={entity === undefined ? `Ajouter une société sur ${site.display_name}` : `${entity.display_name} sur ${site.display_name}`}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      {entity === undefined && (
        <label className="ay-field ay-tenant__wide">
          <span className="ay-field__label">Société</span>
          <select required value={entityId} onChange={(event) => setEntityId(event.target.value)}>
            {candidates.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.display_name}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="ay-field">
        <span className="ay-field__label">SIRET</span>
        <input className="ay-tenant__mono" inputMode="numeric" value={siret} onChange={(event) => setSiret(event.target.value)} />
      </label>
      <label className="ay-field">
        <span className="ay-field__label">Présente depuis le (facultatif)</span>
        <input type="date" value={openedOn} onChange={(event) => setOpenedOn(event.target.value)} />
      </label>
      <fieldset className="ay-tenant__fieldset ay-tenant__wide">
        <legend>Rôles sur le site</legend>
        {profile.vocabulary.roles.map((role) => (
          <label key={role.value} className="ay-tenant__check">
            <input type="checkbox" checked={roles.includes(role.value)} onChange={() => setRoles(roles.includes(role.value) ? roles.filter((entry) => entry !== role.value) : [...roles, role.value])} />
            {text(role.label, role.value)}
          </label>
        ))}
      </fieldset>
      {profile.vocabulary.approval_kinds.map((kind) => (
        <label key={kind.value} className="ay-field">
          <span className="ay-field__label">{text(kind.label, kind.value)} (facultatif)</span>
          <input className="ay-tenant__mono" value={approvals[kind.value] ?? ''} onChange={(event) => setApprovals({ ...approvals, [kind.value]: event.target.value })} />
        </label>
      ))}
      <CommandError error={joinError ?? error} />
      <FormActions
        busy={busy}
        onCancel={onCancel}
        disabled={roles.length === 0 || entityId === ''}
        destructive={
          onCloseEstablishment === undefined ? undefined : (
            <button type="button" className="ay-button ay-button--ghost ay-tenant__destructive" aria-haspopup="dialog" onClick={onCloseEstablishment}>
              Fermer la présence
            </button>
          )
        }
      />
    </form>
  );
}

function CloseForm({ establishment, onDone, onCancel }: { establishment: EstablishmentRow; onDone: () => void; onCancel: () => void }) {
  const { error, busy, run } = useCommand(onDone);
  const [closedOn, setClosedOn] = useState('');
  const [reason, setReason] = useState('');
  return (
    <form
      className="ay-tenant__form"
      aria-label="Fermer la présence"
      onSubmit={(event) => {
        event.preventDefault();
        void run('CloseEstablishment', { payload: { closed_on: closedOn, reason: reason.trim() }, target: { type: 'Establishment', id: establishment.id }, expected_version: establishment.version });
      }}
    >
      <label className="ay-field">
        <span className="ay-field__label">Fermé le</span>
        <input type="date" required value={closedOn} onChange={(event) => setClosedOn(event.target.value)} />
      </label>
      <label className="ay-field">
        <span className="ay-field__label">Motif</span>
        <input required value={reason} onChange={(event) => setReason(event.target.value)} />
      </label>
      <p className="ay-text ay-text--muted ay-tenant__wide">Une présence fermée reste dans l’historique ; elle ne se rouvre pas, on en déclare une nouvelle. Ouvert depuis le {formatDate(establishment.opened_on === undefined ? undefined : `${establishment.opened_on}T12:00:00`)}.</p>
      <CommandError error={error} />
      <FormActions busy={busy} onCancel={onCancel} disabled={closedOn === '' || reason.trim() === ''} />
    </form>
  );
}

/** Une date civile AAAA-MM-JJ lue à midi, pour qu'aucun fuseau ne la décale d'un jour (R1). */
function civilDateLabel(value: string | undefined): string {
  return value === undefined ? '—' : new Date(`${value}T12:00:00`).toLocaleDateString('fr-FR');
}
